import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    Box,
    Typography,
    Dialog,
    RadioGroup,
    FormControlLabel,
    Radio,
    useTheme,
    Checkbox,
} from '@mui/material'
import { useIsSmallScreen } from 'packages/core'
import { IconButton, EidButton, Loader, Tooltip } from 'packages/eid-ui'
import { Icon } from 'packages/eid-icons'
import { ItemDetails } from 'components'
import { TimeDurationControl } from 'components/TimeDurationControl'
import { ReactComponent as GroupIconSvg } from './Resources/group.svg'
import { ReactComponent as OvalIconSvg } from './Resources/oval.svg'
import CredentialsSearch from './AutoCompleteCredentials'
import {
    GetComputerPersonalCredentials,
    GetComputerSharedCredentials,
} from 'hooks'
import moment from 'moment'
import { Theme } from '@mui/material/styles'

const mobileScreenWidth = '767px'

const sectionHeaderSx = (theme: Theme) => ({
    overflowWrap: 'break-word',
    wordBreak: 'normal',
    textTransform: 'uppercase',
    color: `${theme.palette.common.black} !important`,
    overflow: 'hidden',
    lineHeight: '15px',
    fontSize: '14px',
    padding: '15px 31px',
    borderTop: '1px solid rgba(0, 0, 0, .05)',
    borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
    fontWeight: 'bold',
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    backgroundImage:
        'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
    backgroundSize: '8.00px 8.00px',
    '& svg': {
        margin: '0px !important',
    },
})

const bodySx = {
    backgroundColor: '#fbfbfd',
    padding: '24px 40px',
    boxShadow: '0 2px 16px 0 rgba(0, 0, 0, 0.11)',
    display: 'flex',
    '&:focus': { outline: 'none' },
}

const radioBtnSx = {
    display: 'flex',
    flexWrap: 'wrap',
    flexDirection: 'row',
}

const formControlLabelSx = (theme: Theme) => ({
    marginLeft: '0px',
    marginRight: '20px',
    fontSize: '12px',
    color: '#767676',
    height: '30px',
    '&:focus-within': {
        color: theme.palette.common.black,
    },
})

const maxDurationCheckboxSx = {
    display: 'flex',
    alignSelf: 'end',
    '& span': {
        fontSize: '14px',
    },
}

export interface ConnectAdvancedProps {
    open?: any
    onClose?: any
    onReset?: any
    onCancel?: any
    onSucces?: any
    loading?: boolean
    error?: boolean
    requestPolicy: any
    selectedStartDate: any
    setSelectedStartDate: any
    selectedDuration: any
    setSelectedDuration: any
    hasInvalidDates?: boolean
    invalidDatesMessage?: any
    setCredentialsType: any
    credentialsType: any
    item: any
    setSelectedCredentials: any
    selectedCredentials: any
    isConnectedBySomeOneElse: boolean
}

export const ConnectAdvanced: FC<ConnectAdvancedProps> = (props) => {
    const {
        open,
        onClose,
        onSucces,
        loading,
        requestPolicy,
        selectedStartDate,
        setSelectedStartDate,
        selectedDuration,
        setSelectedDuration,
        hasInvalidDates,
        invalidDatesMessage,
        credentialsType,
        setCredentialsType,
        selectedCredentials,
        setSelectedCredentials,
        isConnectedBySomeOneElse,
        item,
    } = props
    const { t } = useTranslation()
    const theme = useTheme()
    const attributeOrientation = useIsSmallScreen() ? 'vertical' : 'horizontal'
    const [credentialsOptions, setCredentialsOptions] = useState<any>([])
    const [showSharedCred, setShowSharedCred] = useState<boolean>(false)
    const [isMaxDurationSelected, setIsMaxDurationSelected] =
        useState<boolean>(false)
    const { data: personalCredentials, isInitialLoading } =
        GetComputerPersonalCredentials()

    const {
        data: sharedCredentials,
        isInitialLoading: loadingSharedCredentials,
    } = GetComputerSharedCredentials(item.id)

    useEffect(() => {
        if (sharedCredentials && !loadingSharedCredentials) {
            setShowSharedCred(sharedCredentials?.availableExist)
        }
    }, [sharedCredentials, loadingSharedCredentials])

    useEffect(() => {
        if (personalCredentials) {
            if (personalCredentials && personalCredentials?.length > 0) {
                bindCredentialOptions(personalCredentials)
            } else {
                setCredentialsType('sharedCredentials')
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [personalCredentials])

    useEffect(() => {
        if (requestPolicy) {
            setSelectedDuration(requestPolicy.defaultValueInMinutes)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestPolicy])

    const invalidCredential =
        credentialsType === 'personalCredentials' && !selectedCredentials

    const bindCredentialOptions = (personalCredentials: any) => {
        let options: any = []
        personalCredentials.forEach((element: any) => {
            options.push({
                id: element.id,
                friendlyName: element.friendlyName,
                value: element.id,
            })
        })
        setCredentialsOptions(options)
    }

    const checkifConnectingNow = () => {
        var a = moment(selectedStartDate)
        var b = moment().format()
        return a.diff(b) <= 0 ? true : false
    }

    const connectedBySomeOneTitle: string = t('Common_ConnectedBySomeElse')

    const disableConnectButton = () => {
        if (
            hasInvalidDates ||
            invalidCredential ||
            loading ||
            (isConnectedBySomeOneElse && checkifConnectingNow())
        ) {
            return true
        }
        return false
    }

    const footerContent = (
        <Box display="flex" width="100%" padding="24px 32px">
            <Tooltip
                title={
                    isConnectedBySomeOneElse && checkifConnectingNow()
                        ? connectedBySomeOneTitle
                        : ''
                }
            >
                <Box minWidth="167px">
                    <EidButton.DrawerActionButton
                        fontColor={theme.palette.common.white}
                        bgColor={theme.palette.primary.main}
                        width="100%"
                        onClick={() =>
                            onSucces(
                                checkifConnectingNow() ? 'Connect' : 'BookNow',
                            )
                        }
                        loading={loading}
                        disabled={disableConnectButton()}
                    >
                        <Box display="flex" alignItems="center">
                            <Icon
                                name="RightArrowWithBorder"
                                color={theme.palette.common.white}
                            />
                        </Box>
                        <Box
                            display="flex"
                            alignItems="center"
                            marginLeft="10px"
                        >
                            <Typography
                                style={{ color: theme.palette.common.white }}
                            >
                                {checkifConnectingNow()
                                    ? t('Connect')
                                    : t('Common_BookNow')}
                            </Typography>
                        </Box>
                    </EidButton.DrawerActionButton>
                </Box>
            </Tooltip>
        </Box>
    )

    const title = t('AdvancedMode')

    function StyledRadio(props: any) {
        return (
            <Radio
                disableRipple
                color="default"
                checkedIcon={
                    <Box display="flex" alignItems="center">
                        <GroupIconSvg color={theme.palette.primary.main} />
                    </Box>
                }
                icon={<OvalIconSvg />}
                {...props}
            />
        )
    }

    let credentialsTypeOptions: any = [
        {
            label: 'Computer_PersonalCredentials',
            value: 'personalCredentials',
        },
    ]

    if (showSharedCred) {
        credentialsTypeOptions = [
            ...credentialsTypeOptions,
            {
                label: 'Computer_SharedCredentials',
                value: 'sharedCredentials',
            },
        ]
    }
    if (item?.isJITEnabled) {
        credentialsTypeOptions = [
            ...credentialsTypeOptions,
            {
                label: 'Computer_JitCredentials',
                value: 'jitCredentials',
            },
        ]
    }

    const handleCredentialsSelect = (e: any) => {
        setCredentialsType(e.target.value)
        if (e.target.value === 'sharedCredentials') {
            setSelectedCredentials(null)
        }
    }

    const handleSelectMaxDuration = (e: any) => {
        setIsMaxDurationSelected(e.target.checked)
        if (e.target.checked) {
            setSelectedDuration(requestPolicy?.maximumValueInMinutes)
        } else {
            if (requestPolicy?.defaultValueInMinutes) {
                setSelectedDuration(requestPolicy?.defaultValueInMinutes)
            }
        }
    }

    return (
        <Dialog
            scroll="body"
            open={open}
            onClose={onClose}
            aria-labelledby="connect-dialog-title"
            PaperProps={{
                sx: {
                    maxWidth: '1000px',
                    width: '840px',
                    [`@media (max-width: ${mobileScreenWidth})`]: {
                        margin: '8px',
                        width: 'calc(100% - 16px) !important',
                        maxWidth: 'calc(100% - 16px) !important',
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    height: 80,
                    alignItems: 'center',
                    padding: '16px 12px 16px 32px',
                    bgcolor: 'background.paper',
                }}
            >
                <Typography
                    id="connect-dialog-title"
                    sx={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: '#3b454d',
                    }}
                >
                    {title}
                </Typography>

                <Box sx={{ display: 'flex' }}>
                    <IconButton
                        onClick={onClose}
                        size="large"
                        aria-label={t('Common_Close')}
                    >
                        <Icon name="Close" color="#959598" />
                    </IconButton>
                </Box>
            </Box>
            {isInitialLoading ? (
                <Box>
                    <Loader />
                </Box>
            ) : (
                <>
                    {credentialsOptions && credentialsOptions.length > 0 && (
                        <>
                            <Box sx={sectionHeaderSx(theme)}>
                                {t('Common_SelectCredentialsType')}
                            </Box>
                            <Box sx={bodySx}>
                                <Box>
                                    <RadioGroup
                                        aria-label={t(
                                            'Common_SelectCredentialsType',
                                        )}
                                        name="credentials-type"
                                        value={credentialsType}
                                        onChange={(e) =>
                                            handleCredentialsSelect(e)
                                        }
                                        sx={radioBtnSx}
                                    >
                                        {credentialsTypeOptions.map(
                                            (item: any, index: number) => (
                                                <FormControlLabel
                                                    key={`credential-type-${index}`}
                                                    value={item.value}
                                                    control={<StyledRadio />}
                                                    label={t(item.label)}
                                                    sx={formControlLabelSx(
                                                        theme,
                                                    )}
                                                    checked={
                                                        credentialsType ===
                                                        item.value
                                                            ? true
                                                            : false
                                                    }
                                                />
                                            ),
                                        )}
                                    </RadioGroup>
                                </Box>
                            </Box>
                            {credentialsType === 'personalCredentials' && (
                                <>
                                    <Box sx={sectionHeaderSx(theme)}>
                                        {t('Common_SelectCredentials')}
                                    </Box>
                                    <Box sx={{ ...bodySx, display: 'block' }}>
                                        <CredentialsSearch
                                            isInitialLoading={isInitialLoading}
                                            credentialsOptions={
                                                credentialsOptions
                                            }
                                            item={item}
                                            selectedCredentials={
                                                selectedCredentials
                                            }
                                            setSelectedCredentials={(e: any) =>
                                                setSelectedCredentials(e)
                                            }
                                        />
                                        {invalidCredential && (
                                            <Box
                                                fontSize="12px"
                                                color="red"
                                                marginTop="10px"
                                            >
                                                {t(
                                                    'Computers_InvalidCredentialError',
                                                )}
                                            </Box>
                                        )}
                                    </Box>
                                </>
                            )}
                        </>
                    )}
                </>
            )}

            <Box sx={sectionHeaderSx(theme)}>{t('SetDuration')}</Box>
            <Box sx={bodySx}>
                <Box>
                    <ItemDetails.Attribute
                        label={t('DefaultAccessDurationInMinutes')}
                        labelProps={{ marginRight: '10px' }}
                        value={requestPolicy?.defaultValueInMinutes}
                        orientation={attributeOrientation}
                        valueProps={{ maxHeight: '220px', overflow: 'auto' }}
                    />
                    <ItemDetails.Attribute
                        label={t('MaxAccessDurationInMinutes')}
                        labelProps={{ marginRight: '10px' }}
                        value={requestPolicy?.maximumValueInMinutes}
                        orientation={attributeOrientation}
                        valueProps={{ maxHeight: '220px', overflow: 'auto' }}
                    />
                    <ItemDetails.Attribute
                        label={t('SetDuration')}
                        labelProps={{
                            style: {
                                marginRight: '10px',
                                alignSelf: 'center',
                                minWidth: '100px',
                            },
                        }}
                        value={
                            <>
                                <Box display="flex">
                                    <TimeDurationControl
                                        disabled={
                                            !requestPolicy?.isEndDateSelectable ||
                                            isMaxDurationSelected ||
                                            (isConnectedBySomeOneElse &&
                                                checkifConnectingNow())
                                        }
                                        selectedStartDate={selectedStartDate}
                                        setSelectedStartDate={
                                            setSelectedStartDate
                                        }
                                        selectedDuration={selectedDuration}
                                        setSelectedDuration={
                                            setSelectedDuration
                                        }
                                        maximumDuration={
                                            requestPolicy?.maximumValueInMinutes
                                        }
                                    />
                                    {requestPolicy?.maximumValueInMinutes && (
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    edge="start"
                                                    color="primary"
                                                    onChange={(e) => {
                                                        handleSelectMaxDuration(
                                                            e,
                                                        )
                                                    }}
                                                    checked={
                                                        isMaxDurationSelected
                                                    }
                                                    style={{
                                                        marginLeft: '8px',
                                                        padding: '8px',
                                                    }}
                                                />
                                            }
                                            label={t('Common_MaxDuration')}
                                            sx={maxDurationCheckboxSx}
                                        />
                                    )}
                                </Box>

                                {hasInvalidDates && invalidDatesMessage && (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            fontSize: '12px',
                                            color: theme.palette.error.main,
                                            marginRight: '12px',
                                        }}
                                    >
                                        {invalidDatesMessage}
                                    </Box>
                                )}
                            </>
                        }
                        orientation={attributeOrientation}
                        valueProps={{ maxHeight: '220px', overflow: 'auto' }}
                    />
                </Box>
            </Box>

            <Box>{footerContent}</Box>
        </Dialog>
    )
}
